<template>
  <div
    v-if="isAdmin"
    class="has-background-ghost-grey m-t-lg"
  >
    <div class="content">
      <div
        v-if="!hasNOV && failingOnes.length"
        class="section"
      >
        <div
          v-if="!radyForWarning"
          class="columns"
        >
          <div class="column has-text-centered">
            <button
              :class="{ 'is-loading': creatingWarning }"
              :disabled="creatingWarning"
              class="button"
              @click="initiateWarning"
            >
              Issue a NOV
            </button>
          </div>
        </div>
        <div
          v-else
          class="columns is-centered"
        >
          <div class="column is-half">
            <h3>Issue a notice of violation <help-icon modal="basic-lead-disclosure-info" /></h3>
            <p>Issue a notice of violation to notify the landlord or daycare operator that their property is not in compliance with lead certification requirements and that they have 30 days to comply before incurring fines. A hard copy must be sent in the mail the same day the NOV is issued in the online system. Review all submitted certifications before issuing the NOV.</p>
            <validation-observer
              ref="create-warning"
              class="is-fullwidth"
              @submit.prevent="false"
            >
              <div class="field">
                <p class="label">
                  Enter additional comments for the landlord
                  <!--
                  Section removed in the latest update.
                  <br>
                  <help-icon modal="letter-example">
                    <small>E-mail example</small>
                  </help-icon> -->
                </p>
                <div class="control">
                  <textarea-with-validation
                    v-model="statusComment"
                    rows="2"
                    placeholder="Enter additional comments for the landlord"
                    name="ReasonFailureText"
                  />
                </div>
              </div>
              <div class="field">
                <div class="control top">
                  <label for="">
                    <p class="label">Deadline</p>
                    <p>By default landlords have 30 days to correct all issues and be in compliance before incurring fines.</p>
                    <datepicker
                      v-model="graceDate"
                      :disabled-dates="disabledDates"
                      name="graceDate"
                      placeholder="Grace date"
                      input-class="input"
                    />
                  </label>
                </div>
              </div>
              <div class="field">
                <div class="control">
                  <p class="label m-b-none">
                    Select units to be issued the notice of violation.
                  </p>
                  <p>Only units currently with a status of rejected or failed can be issued a violation.</p>
                  <table
                    class="table is-middle is-bordered is-hoverable is-fullwidth fixed"
                  >
                    <thead>
                      <th>Unit name</th>
                      <th>Failure description</th>
                      <th>Status</th>
                    </thead>
                    <tbody>
                      <tr
                        v-for="failed in failingOnes"
                        :key="failed.id"
                      >
                        <td class="nowrap is-status">
                          <checkbox
                            v-model="beingWarned"
                            :value="failed.id"
                          >
                            {{ failed.name }}
                          </checkbox>
                        </td>
                        <td>
                          <span v-if="failed.status === 'Expired'">
                            The lead safe certificate has expired.
                          </span>
                          <span v-else>
                            {{ failed.statusReason === "Other" ? failed.statusComment : failed.statusReason }}
                          </span>
                        </td>
                        <td>{{ failed.status }}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
              <div class="field is-grouped is-grouped-centered">
                <div class="control">
                  <button
                    class="button update-button"
                    :disabled="creatingWarning"
                    :class="{ 'is-loading': creatingWarning }"
                    @click="createWarning"
                  >
                    Submit
                  </button>
                </div>
              </div>
            </validation-observer>
          </div>
        </div>
      </div>
      <div
        v-else-if="hasNOV"
        class="section"
      >
        <div class="columns">
          <div class="column has-text-centered">
            <p>
              A Notice of Violation has been issued for this lead disclosure. View the NOV
              <router-link :to="{ name: 'single-case', params: { uuid: hasNOV.id }}">
                here.
              </router-link>
            </p>
          </div>
        </div>
      </div>
    </div>
    <more-info
      id="basic-lead-disclosure-info"
      title="Issuing an NOV"
    >
      <p>Note that failed or rejected lead certification may not mean the property is out of compliance. Check that the following information indicates noncompliance: </p>
      <basic-lead-disclosure-info
        :eligibility-check="eligibilityCheck"
        :found-license-data="foundLicenseData"
        :selected-property="selectedProperty"
      />
    </more-info>

    <!--
    Section removed in the latest update.
    <more-info
      id="letter-example"
      name="E-mail example"
    >
      <p>Hello {{ `${eligibilityCheck.submitter.firstName} ${eligibilityCheck.submitter.lastName}` }},</p>
      <p>
        There are one or more problems with the lead disclosure you submitted for the property <strong>{{ selectedProperty.street_address }} - {{ selectedProperty.zip_code }}</strong> on <strong>{{ $dayjs(eligibilityCheck.submittedAt).format('MM/DD/YYYY') }}</strong>. As a result, your property is not in compliance with lead certification requirements. You can read more about those requirements. Login to your account to view the case and make any necessary corrections. If you have questions please <router-link :to="{ name: 'contact-us' }">
          contact
        </router-link> the Lead and Healthy Homes Program.
      </p>
      <p>You must submit corrected documentation including a lead-safe certificate and dust wipe lab report or lead-free certificate with a full inspection report by <strong>{{ $dayjs(graceDate).format('MM/DD/YYYY') }}</strong> pursuant to the Philadelphia Lead Disclosure and Certification Law.</p>
      <p>If you fail to provide the required documentation, the City will seek further enforcement action, including the imposition of fines and possible revocation of your rental license.</p>
      <p>Failure to submit correct and complete documentation certifying your property as lead-free or lead-safe by the date stated above will result in further penalties being imposed against you including an initial $150 fine and possible revocation of your rental license pursuant to Section 6-811 of Title 6 of the Philadelphia Code. Repeated failure to comply will result in further penalties including additional fines.</p>
      <p>Pursuant to Philadelphia Code § 6-504.1, any person who is aggrieved by a decision and/or an order directed to them or requiring any action, forbearance, or compliance from them may appeal to the Board of License and Inspection Review within thirty days of the date of this notice. Appeals must be submitted in writing on approved forms to the Boards Administration 11th Floor Municipal Services Building, 1401 John F. Kennedy Blvd Philadelphia, PA 19102.</p>
      <p>IF YOU DO NOT TIMELY FILE AN APPEAL THEN YOU WAIVE YOUR RIGHT TO LATER CHALLENGE ANY PART OF THIS NOTICE. This includes waiving your right to argue: whether the conditions existed, whether you were responsible to correct the conditions, whether you had enough time to correct the conditions, whether the City should have rejected your license, whether the City can impose fines, fines, or costs against you, and any other arguments you may wish to make regarding the validity of this notice and order.</p>
      <p>You will receive an official letter of warning from the Philadelphia Department of Health sent to <strong>{{ letterAddress }}</strong></p>
      <p>Date of notice: <strong>{{ $dayjs().format('MM/DD/YYYY') }}</strong></p>

      <div v-if="statusComment">
        <h3>Additional comments:</h3>
        <p>{{ statusComment }}</p>
      </div>
    </more-info> -->
  </div>
</template>

<script>
import Checkbox from "@/components/common/Checkbox";
import TextareaWithValidation from '@/components/common/TextareaWithValidation';
import Datepicker from 'vuejs-datepicker';
import MoreInfo from '@/components/modals/MoreInfo.vue';
import HelpIcon from '@/components/common/HelpIcon.vue';
import BasicLeadDisclosureInfo from '@/components/common/BasicLeadDisclosureInfo.vue';
import EligibilityCheckInfo from '@/mixins/eligiblityCheckInfo';

export default {
  name: "IssueWarning",
  components: {
    Checkbox,
    Datepicker,
    TextareaWithValidation,
    MoreInfo,
    HelpIcon,
    BasicLeadDisclosureInfo,
  },
  extends: EligibilityCheckInfo,
  props: {
    inspections: {
      type: Array,
      default: () => ([]),
    },
  },
  data() {
    return {
      showWarnedDetails: false,
      beingWarned: [],
      radyForWarning: false,
      showWarningForm: false,
      creatingWarning: false,

      statusComment: '',
      graceDate: null,
      disabledDates: {
        to: new Date(),
      },
    };
  },
  computed: {
    hasNOV() {
      return this.$store.getters['cases/hasNOV'](this.eligibilityCheck.id);
    },
    failingOnes() {
      return this.inspections.filter(insp => !/Passed|Pending/gi.test(insp.status));
    },
    letterAddress() {
      if (this.foundLicenseData.business_mailing_address) {
        return this.foundLicenseData.business_mailing_address;
      }
      return this.proprietorAddress;
    },
  },
  beforeMount() {
    let time = new Date();
    time.setDate(time.getDate()+30);
    this.graceDate = time;
  },
  methods: {
    async initiateWarning() {
      this.creatingWarning = true;
      await this.$store.dispatch('properties/getInspections', { uuid: this.$route.params.uuid, forceUpdate: true });

      if (!this.failingOnes.length) {
        this.$warning("You will need to fail at least one inspection in order to create a warning");
        this.creatingWarning = false;
        return;
      }

      this.beingWarned = this.failingOnes.map(insp => insp.id);

      this.radyForWarning = true;
      this.creatingWarning = false;
    },

    async createWarning() {
      const valid = await this.$refs['create-warning'].validate();
      if (!valid) {
        return;
      }

      if (this.beingWarned.length === 0) {
        this.$warning("You will need to fail at least one inspection in order to create a warning");
        return;
      }
      
      this.creatingWarning = true;

      const submitData = { 
        eligibilityCheckId: this.eligibilityCheck.id,
        caseComment: this.statusComment,
        graceEndDate: this.graceDate,
        excludedInspections: this.failingOnes.filter(insp => !this.beingWarned.includes(insp.id)).map(insp => insp.id),
      };

      console.log(submitData);

      try {
        await this.$store.dispatch('cases/submitWarningNOV', submitData);
        this.$forceUpdate();
      } catch (err) {
        this.$fatalError();
      }

      this.creatingWarning = false;
    },
  },
};
</script>

<style>

</style>